// NotificationBell — AppShell top bar'daki zil ikonu.
// Dropdown'da son 10 bildirim + unread badge. SSE ile realtime update.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { NotificationType } from "@mahir/shared";
import { useNotificationsStore } from "../../stores/notifications-store";

// Tip → ikon/renk (Geçmiş Rapor Aktarımı, Task 15). Bugüne kadar hiçbir tip ikon
// taşımıyordu; yalnız aktarım bildirimleri için eklendi — kuyruğu saniyeler/dakikalar
// sonra biten bir işin sonucu listede bir bakışta ayırt edilebilmeli. Haritada
// OLMAYAN tipler bugünküyle birebir aynı (ikonsuz) kalır.
const TYPE_VISUALS: Partial<Record<NotificationType, { icon: string; color: string }>> = {
  report_import_completed: { icon: "📄", color: "var(--color-success)" },
  report_import_failed: { icon: "⚠", color: "var(--color-error)" },
  // KPI M16 T19 (DR6) — müşteri döngüsü
  kpi_value_verified: { icon: "✓", color: "var(--color-success)" },
  kpi_clarification_requested: { icon: "?", color: "var(--color-warning-text)" },
  kpi_client_submitted: { icon: "📊", color: "var(--color-primary)" },
  // TODO-KPI-21 — danışman müşterinin girdiği değeri düzeltti
  kpi_value_corrected: { icon: "✎", color: "var(--color-warning-text)" },
  kpi_client_note: { icon: "✉", color: "var(--color-primary)" },
  kpi_consultant_reply: { icon: "✉", color: "var(--color-primary)" },
};

export function NotificationBell(): JSX.Element {
  const notifications = useNotificationsStore((s) => s.notifications);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const load = useNotificationsStore((s) => s.load);
  const markRead = useNotificationsStore((s) => s.markRead);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);
  const connectSSE = useNotificationsStore((s) => s.connectSSE);
  const disconnectSSE = useNotificationsStore((s) => s.disconnectSSE);
  const sseConnected = useNotificationsStore((s) => s.sseConnected);

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    void load();
    connectSSE();
    return () => {
      disconnectSSE();
    };
  }, [load, connectSSE, disconnectSSE]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
    };
  }, [open]);

  const handleClick = (notifId: string, url: string | null): void => {
    void markRead(notifId);
    setOpen(false);
    if (url) void navigate(url);
  };

  return (
    <div ref={menuRef} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((s) => !s)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Bildirimler${unreadCount > 0 ? ` (${unreadCount.toString()} okunmamış)` : ""}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 36,
          height: 36,
          borderRadius: 999,
          border: "1px solid var(--color-border)",
          background: open ? "rgba(15,42,61,0.04)" : "var(--color-bg)",
          cursor: "pointer",
          position: "relative",
        }}
        title={sseConnected ? "Canlı bağlı" : "Offline — yenile"}
      >
        <span style={{ fontSize: 16, color: "var(--color-text)" }}>🔔</span>
        {unreadCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              padding: "0 4px",
              borderRadius: 9,
              background: "var(--color-error)",
              color: "var(--color-surface)",
              fontSize: "0.65rem",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid var(--color-surface)",
            }}
          >
            {unreadCount > 99 ? "99+" : unreadCount.toString()}
          </span>
        )}
        {!sseConnected && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              bottom: -2,
              right: -2,
              width: 8,
              height: 8,
              borderRadius: 4,
              background: "var(--color-text-3)",
              border: "1px solid var(--color-surface)",
            }}
            title="SSE bağlantısı yok"
          />
        )}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 6px)",
            width: "min(360px, calc(100vw - 24px))",
            maxHeight: 480,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: 10,
            boxShadow: "var(--shadow-md)",
            padding: 0,
            zIndex: 30,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 12px",
              borderBottom: "1px solid var(--color-border)",
            }}
          >
            <strong style={{ fontSize: "0.88rem", color: "var(--color-text)" }}>Bildirimler</strong>
            {unreadCount > 0 && (
              <button
                onClick={() => void markAllRead()}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--color-primary)",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                Hepsini okundu yap
              </button>
            )}
          </div>

          <div style={{ overflow: "auto", flex: 1 }}>
            {notifications.length === 0 ? (
              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                  fontSize: "0.82rem",
                  color: "var(--color-text-3)",
                }}
              >
                Henüz bildirim yok.
              </div>
            ) : (
              notifications.slice(0, 10).map((n) => {
                const isUnread = n.readAt === null;
                // `Notification.type` şemada `string` (ileri uyumluluk) — haritada
                // olmayan tip `undefined` döner, o satır bugünküyle aynı kalır.
                const visual = TYPE_VISUALS[n.type as NotificationType];
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n.id, n.url)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "10px 12px",
                      background: isUnread ? "rgba(15,42,61,0.04)" : "transparent",
                      border: "none",
                      borderBottom: "1px solid var(--color-border)",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(15,42,61,0.07)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = isUnread
                        ? "rgba(15,42,61,0.04)"
                        : "transparent";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.85rem",
                          color: "var(--color-text)",
                          fontWeight: isUnread ? 600 : 400,
                        }}
                      >
                        {visual ? (
                          <span aria-hidden="true" style={{ color: visual.color, marginRight: 6 }}>
                            {visual.icon}
                          </span>
                        ) : null}
                        {n.title}
                      </span>
                      <span
                        style={{
                          fontSize: "0.7rem",
                          color: "var(--color-text-3)",
                          fontFamily: "var(--font-mono)",
                          flexShrink: 0,
                        }}
                      >
                        {formatTime(n.createdAt)}
                      </span>
                    </div>
                    {n.body && (
                      <p
                        style={{
                          fontSize: "0.78rem",
                          color: "var(--color-text-2)",
                          marginTop: 3,
                          marginBottom: 0,
                          lineHeight: 1.4,
                        }}
                      >
                        {n.body}
                      </p>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {notifications.length > 10 && (
            <div
              style={{
                padding: "8px 12px",
                borderTop: "1px solid var(--color-border)",
                fontSize: "0.75rem",
                color: "var(--color-text-3)",
                textAlign: "center",
              }}
            >
              Son 10 gösteriliyor — {notifications.length.toString()}/
              {notifications.length.toString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "şimdi";
  if (min < 60) return `${min.toString()}dk`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr.toString()}sa`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day.toString()}g`;
  return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
}
